import { parseResultValue, convertFxRange } from './GenericUtil';


const MAJOR_WEIGHT_THRESHOLD = 0.15;
const CONFIDENCE_CEILING = 95;
const SCORE_CEILING = 95;
const LOW_CONFIDENCE_THRESHOLD = 20;

/**
 * Calculate a score (0-100) for a single marker based on how close it is to functional range
 * @param {number} result - The test result value
 * @param {number} fxLow - Functional range low
 * @param {number} fxHigh - Functional range high
 * @param {number} labLow - Lab reference range low
 * @param {number} labHigh - Lab reference range high
 * @returns {number} Score from 0-100
 */
export function calculateMarkerScore(result, fxLow, fxHigh, labLow, labHigh) {
  if (result == null || (fxLow == null && fxHigh == null)) {
    return null;
  }

  const numResult = parseResultValue(result);

  // If result is within functional range: 100 points
  if ((fxLow == null || numResult >= fxLow) && (fxHigh == null || numResult <= fxHigh)) {
    return 100;
  }

  // Below functional range
  if (fxLow != null && numResult < fxLow) {
    const effectiveLabLow = labLow != null ? labLow : 0;

    // "diminished" (between fx and lab): 50-100
    if (labLow == null || numResult >= labLow) {
      const range = fxLow - effectiveLabLow;
      if (range <= 0) return 75; // Edge case
      const distance = fxLow - numResult;
      const severity = Math.min(distance / range, 1);
      return Math.round(100 - (severity * 50));
    }

    // "low" (below lab): 0-50
    // Further below lab = lower score
    const distanceBelowLab = labLow - numResult;
    const maxBelowLab = labLow * 0.5; // Assume critical at 50% below lab low
    const severity = Math.min(distanceBelowLab / maxBelowLab, 1);
    return Math.round(50 * (1 - severity));
  }

  // Above functional range
  if (fxHigh != null && numResult > fxHigh) {
    const effectiveLabHigh = labHigh != null ? labHigh : fxHigh * 2;

    // "elevated" (between fx and lab): 50-100
    if (labHigh == null || numResult <= labHigh) {
      const range = effectiveLabHigh - fxHigh;
      if (range <= 0) return 75; // Edge case
      const distance = numResult - fxHigh;
      const severity = Math.min(distance / range, 1);
      return Math.round(100 - (severity * 50));
    }

    // "high" (above lab): 0-50
    const distanceAboveLab = numResult - labHigh;
    const maxAboveLab = labHigh * 0.5; // Assume critical at 50% above lab high
    const severity = Math.min(distanceAboveLab / maxAboveLab, 1);
    return Math.round(50 * (1 - severity));
  }

  return 100;
}

/**
 * Aggregate marker scores into a system score
 * Abnormal markers (score < 80) get 1.5x weight to surface issues
 * @param {Array<{name: string, score: number}>} markerScores - Array of marker scores
 * @returns {number|null} System score 0-100, or null if no markers
 */
export function calculateSystemScore(markerScores) {
  const validScores = markerScores.filter(m => m.score != null);

  if (validScores.length === 0) {
    return null;
  }

  // Weighted average: abnormal markers have more influence
  const weights = validScores.map(m => m.score < 80 ? 1.5 : 1.0);
  const weightedSum = validScores.reduce((sum, m, i) => sum + m.score * weights[i], 0);
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);

  const rawScore = Math.round(weightedSum / totalWeight);
  return Math.min(rawScore, SCORE_CEILING);
}

/**
 * Calculate confidence rating for a system based on present markers
 * @param {Array<string>} presentMarkers - Marker names present in the test
 * @param {Object} systemMarkers - System marker config { markerName: { confidence_weight } }
 * @returns {Object} Confidence data including percentage and missing markers
 */
export function calculateConfidence(presentMarkers, systemMarkers) {
  let rawConfidence = 0;
  const missingMarkers = [];
  const presentMarkersList = [];

  for (const [markerName, config] of Object.entries(systemMarkers)) {
    if (presentMarkers.includes(markerName.toLowerCase())) {
      rawConfidence += config.confidence_weight;
      presentMarkersList.push({
        name: markerName,
        weight: config.confidence_weight
      });
    } else {
      missingMarkers.push({
        name: markerName,
        weight: config.confidence_weight
      });
    }
  }

  // Group missing markers by importance
  const majorMissing = missingMarkers
    .filter(m => m.weight >= MAJOR_WEIGHT_THRESHOLD)
    .map(m => m.name);
  const minorMissing = missingMarkers
    .filter(m => m.weight < MAJOR_WEIGHT_THRESHOLD)
    .map(m => m.name);

  // Display confidence capped at 95%
  const displayConfidence = Math.min(Math.round(rawConfidence * 100), CONFIDENCE_CEILING);

  return {
    percentage: displayConfidence,
    rawPercentage: Math.round(rawConfidence * 100),
    presentCount: presentMarkersList.length,
    totalMarkers: Object.keys(systemMarkers).length,
    missingMarkers: {
      major: majorMissing,
      minor: minorMissing
    },
    presentMarkers: presentMarkersList,
    isLowConfidence: displayConfidence < LOW_CONFIDENCE_THRESHOLD
  };
}

/**
 * Interpolate between two hex colors
 * @param {string} color1 - Start hex color
 * @param {string} color2 - End hex color
 * @param {number} t - Interpolation factor (0-1)
 * @returns {string} Interpolated hex color
 */
function interpolateColor(color1, color2, t) {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);
  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);

  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Site color palette - matches status tag colors
const COLORS = {
  green: { r: 34, g: 197, b: 94 },    // #22c55e - optimal
  yellow: { r: 234, g: 179, b: 8 },   // #eab308 - dysfunctional
  red: { r: 239, g: 68, b: 68 }       // #ef4444 - abnormal
};

/**
 * Get score color based on status tag colors with opacity gradient
 * 85-95: green (optimal) - 75% to 100% opacity
 * 50-84: yellow (dysfunctional) - 75% to 100% opacity
 * 0-49: red (abnormal) - 75% to 100% opacity
 * @param {number|null} score - Score 0-95 or null
 * @returns {string} RGBA color string
 */
export function getScoreColor(score) {
  if (score == null) return 'rgba(156, 163, 175, 1)'; // Gray - insufficient data

  const clampedScore = Math.max(0, Math.min(score, SCORE_CEILING));

  // 85-95: green (optimal) - opacity 75% at 85, 100% at 95
  if (clampedScore >= 85) {
    const t = (clampedScore - 85) / 10; // 0 at 85, 1 at 95
    const opacity = 0.75 + (t * 0.25);
    return `rgba(${COLORS.green.r}, ${COLORS.green.g}, ${COLORS.green.b}, ${opacity})`;
  }

  // 50-84: yellow (dysfunctional) - opacity 75% at 50, 100% at 84
  if (clampedScore >= 50) {
    const t = (clampedScore - 50) / 34; // 0 at 50, 1 at 84
    const opacity = 0.75 + (t * 0.25);
    return `rgba(${COLORS.yellow.r}, ${COLORS.yellow.g}, ${COLORS.yellow.b}, ${opacity})`;
  }

  // 0-49: red (abnormal) - opacity 75% at 0, 100% at 49
  const t = clampedScore / 49; // 0 at 0, 1 at 49
  const opacity = 0.75 + (t * 0.25);
  return `rgba(${COLORS.red.r}, ${COLORS.red.g}, ${COLORS.red.b}, ${opacity})`;
}

/**
 * Get score label based on value
 * @param {number|null} score - Score 0-100 or null
 * @returns {string} Human-readable label
 */
export function getScoreLabel(score) {
  if (score == null) return 'N/A';
  if (score >= 90) return 'Optimal';
  if (score >= 70) return 'Attention';
  if (score >= 50) return 'Concern';
  return 'Action Needed';
}

/**
 * Calculate all system scores from test data
 * @param {Array<Object>} tests - Array of test objects with alias, result, lab_low, lab_high
 * @param {Object} systemsConfig - Systems configuration from blood_groupings.json
 * @param {Function} getFxRange - Function to get functional range for a marker
 * @returns {Object} Map of systemId -> { score, confidence, markerScores }
 */
export function calculateAllSystemScores(tests, systemsConfig, getFxRange) {
  const results = {};

  // Build a map of present markers and their values (lowercase keys for case-insensitive matching)
  const testMap = {};
  tests.forEach(test => {
    const name = test.alias || test.test_name;
    const key = name.toLowerCase();
    if (!testMap[key]) testMap[key] = test;
  });

  const presentMarkerKeys = Object.keys(testMap);

  for (const [systemId, systemConfig] of Object.entries(systemsConfig)) {
    const markerScores = [];

    for (const markerName of Object.keys(systemConfig.markers)) {
      const test = testMap[markerName.toLowerCase()];

      if (test) {
        const rawFxRange = getFxRange(markerName);
        const fxRange = convertFxRange(rawFxRange, markerName, test.units);
        const score = calculateMarkerScore(
          test.result,
          fxRange?.fx_low,
          fxRange?.fx_high,
          test.lab_low,
          test.lab_high
        );

        markerScores.push({
          name: markerName,
          score,
          result: test.result,
          fxRange
        });
      }
    }

    const systemScore = calculateSystemScore(markerScores);
    const confidence = calculateConfidence(presentMarkerKeys, systemConfig.markers);

    results[systemId] = {
      id: systemId,
      displayName: systemConfig.display_name,
      icon: systemConfig.icon,
      score: confidence.isLowConfidence ? null : systemScore,
      confidence,
      markerScores,
      color: getScoreColor(confidence.isLowConfidence ? null : systemScore),
      label: getScoreLabel(confidence.isLowConfidence ? null : systemScore)
    };
  }

  return results;
}
